/**
 * RAG (Retrieval Augmented Generation) service
 * Handles semantic search of policy embeddings AND knowledge base documents
 */

import { supabase } from '@/integrations/supabase/client';
import { generateEmbedding } from './embedding-service';
import { PolicyChunk, formatRAGContext } from './vector-utils';

export interface SearchResult extends PolicyChunk {
  policy_id: string;
  policy_title: string;
  chunk_heading: string;
  chunk_content: string;
  similarity_score: number;
  chunk_index: number;
}

/**
 * Search for relevant approved policy chunks using semantic similarity
 * 
 * @param query - User's search query
 * @param companyId - Company ID for isolation
 * @param limit - Maximum number of chunks to return (default: 5)
 * @param similarityThreshold - Minimum similarity score (0-1, default: 0.3)
 * @returns Array of relevant policy chunks sorted by similarity
 */
export async function searchApprovedPolicies(
  query: string,
  companyId: string,
  limit: number = 5,
  similarityThreshold: number = 0.3
): Promise<SearchResult[]> {
  if (!query || !query.trim()) {
    return [];
  }

  if (!companyId) {
    throw new Error('Company ID is required for policy search');
  }

  try {
    // Generate embedding for the query
    const { embedding: queryEmbedding } = await generateEmbedding(query);

    // Perform vector similarity search
    // Using RPC function for efficient vector search with company isolation
    const { data, error } = await supabase.rpc('search_policy_embeddings', {
      query_embedding: queryEmbedding,
      match_threshold: similarityThreshold,
      match_count: limit,
      filter_company_id: companyId
    });

    if (error) {
      console.error('Error searching policy embeddings:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    // Transform results
    const results: SearchResult[] = data.map((row: any) => ({
      policy_id: row.policy_id,
      policy_title: row.policy_title || 'Untitled Policy',
      chunk_heading: row.chunk_heading,
      chunk_content: row.chunk_content,
      similarity_score: row.similarity,
      chunk_index: row.chunk_index
    }));

    console.log('[RAG Service] Similarity scores:', results.map(r => ({
      heading: r.chunk_heading,
      score: r.similarity_score.toFixed(3)
    })));

    return results;
  } catch (error: any) {
    console.error('Error in searchApprovedPolicies:', error);
    throw new Error(`Failed to search policies: ${error.message}`);
  }
}

// getRAGContext is defined at the bottom of this file (combines policy + KB search)

/**
 * Get all chunks for a specific policy (for debugging/display)
 * 
 * @param policyId - Policy ID
 * @param companyId - Company ID for security
 * @returns Array of chunks for the policy
 */
export async function getPolicyChunks(
  policyId: string,
  companyId: string
): Promise<SearchResult[]> {
  try {
    const { data, error } = await supabase
      .from('policy_embeddings')
      .select(`
        policy_id,
        chunk_index,
        chunk_heading,
        chunk_content,
        policies!inner(title, company_id)
      `)
      .eq('policy_id', policyId)
      .eq('company_id', companyId)
      .order('chunk_index', { ascending: true });

    if (error) {
      console.error('Error fetching policy chunks:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      return [];
    }

    return data.map((row: any) => ({
      policy_id: row.policy_id,
      policy_title: row.policies.title || 'Untitled Policy',
      chunk_heading: row.chunk_heading,
      chunk_content: row.chunk_content,
      similarity_score: 1.0, // Not from similarity search
      chunk_index: row.chunk_index
    }));
  } catch (error: any) {
    console.error('Error in getPolicyChunks:', error);
    throw new Error(`Failed to get policy chunks: ${error.message}`);
  }
}

/**
 * Check if a policy has embeddings
 * 
 * @param policyId - Policy ID
 * @returns True if policy has embeddings
 */
export async function policyHasEmbeddings(policyId: string): Promise<boolean> {
  try {
    const { count, error } = await supabase
      .from('policy_embeddings')
      .select('id', { count: 'exact', head: true })
      .eq('policy_id', policyId);

    if (error) {
      console.error('Error checking policy embeddings:', error);
      return false;
    }

    return (count ?? 0) > 0;
  } catch (error) {
    console.error('Error in policyHasEmbeddings:', error);
    return false;
  }
}

/**
 * Get embedding statistics for a company
 * 
 * @param companyId - Company ID
 * @returns Statistics about embeddings
 */
export async function getEmbeddingStats(companyId: string) {
  try {
    const { data, error } = await supabase
      .from('policy_embeddings')
      .select('policy_id, token_count', { count: 'exact' })
      .eq('company_id', companyId);

    if (error) {
      throw error;
    }

    const uniquePolicies = new Set(data?.map(d => d.policy_id) || []).size;
    const totalChunks = data?.length || 0;
    const totalTokens = data?.reduce((sum, d) => sum + (d.token_count || 0), 0) || 0;

    return {
      totalPolicies: uniquePolicies,
      totalChunks,
      totalTokens,
      avgChunksPerPolicy: uniquePolicies > 0 ? Math.round(totalChunks / uniquePolicies) : 0,
      avgTokensPerChunk: totalChunks > 0 ? Math.round(totalTokens / totalChunks) : 0
    };
  } catch (error: any) {
    console.error('Error getting embedding stats:', error);
    throw new Error(`Failed to get embedding stats: ${error.message}`);
  }
}

// ─── Knowledge base search ─────────────────────────────────────────────────

export interface KnowledgeBaseSearchResult {
  document_id: string;
  document_name: string;
  chunk_heading: string;
  chunk_content: string;
  similarity_score: number;
}

/**
 * Search knowledge base documents using semantic similarity.
 */
export async function searchKnowledgeBase(
  query: string,
  companyId: string,
  limit: number = 5,
  similarityThreshold: number = 0.3
): Promise<KnowledgeBaseSearchResult[]> {
  if (!query?.trim() || !companyId) return [];

  try {
    const { embedding: queryEmbedding } = await generateEmbedding(query);

    const { data, error } = await supabase.rpc('search_knowledge_base_embeddings', {
      query_embedding: queryEmbedding,
      match_company_id: companyId,
      match_threshold: similarityThreshold,
      match_count: limit,
    });

    if (error) {
      console.error('[RAG] KB search error:', error);
      return [];
    }

    return (data ?? []).map((row: any) => ({
      document_id: row.document_id,
      document_name: row.document_name || 'Unknown Document',
      chunk_heading: row.chunk_heading,
      chunk_content: row.chunk_content,
      similarity_score: row.similarity,
    }));
  } catch (error: any) {
    console.error('[RAG] KB search failed:', error);
    return [];
  }
}

/**
 * Format knowledge base search results for injection into a system prompt.
 */
function formatKBContext(results: KnowledgeBaseSearchResult[]): string {
  if (results.length === 0) return '';

  const lines: string[] = ['### Retrieved Regulatory & Compliance Documents\n'];

  for (const r of results) {
    lines.push(`**Document:** ${r.document_name}`);
    if (r.chunk_heading && r.chunk_heading !== r.document_name) {
      lines.push(`**Section:** ${r.chunk_heading}`);
    }
    lines.push(`**Relevance:** ${Math.round(r.similarity_score * 100)}%`);
    lines.push('');
    lines.push(r.chunk_content);
    lines.push('\n---\n');
  }

  return lines.join('\n');
}

/**
 * Combined RAG context: approved policies + knowledge base documents.
 * Runs both searches in parallel and merges results.
 */
export async function getRAGContext(
  query: string,
  companyId: string,
  limit: number = 5
): Promise<string> {
  try {
    console.log('[RAG] Searching for:', query);

    const [policyResults, kbResults] = await Promise.all([
      searchApprovedPolicies(query, companyId, limit).catch(() => []),
      searchKnowledgeBase(query, companyId, limit).catch(() => []),
    ]);

    console.log(`[RAG] Policy chunks: ${policyResults.length}, KB chunks: ${kbResults.length}`);

    const parts: string[] = [];

    if (policyResults.length > 0) {
      parts.push(formatRAGContext(policyResults));
    }

    if (kbResults.length > 0) {
      parts.push(formatKBContext(kbResults));
    }

    return parts.join('\n\n');
  } catch (error: any) {
    console.error('[RAG] Error:', error);
    return '';
  }
}

